import React, { useEffect, useState, useRef, useMemo, PureComponent, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { Spinner, mergeClasses } from '@fluentui/react-components';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { PlayPauseButton } from './play-pause-button';
import { Scrubber } from './scrubber';
import { VolumeControls } from './volume-controls';
import { TimeAndDuration } from './time-and-duration';
import { useStyles } from './style';
import type {
  ClipchampAsset,
  ClipchampAssetFile,
  ClipchampPlayerModule,
  ClipchampPlayerOptions,
  ClipchampPlayerProps,
  ClipchampProject,
  ErrorBoundaryProps,
  ErrorBoundaryState,
} from '../types';

export const PREVIEW_TYPES = {
  MP4: 'MP4',
  CLIPCHAMP: 'CLIPCHAMP',
};

function withPermanentUrl(assetResolver: (id: string) => Promise<[ClipchampAssetFile, ClipchampAsset]>) {
  return async (id: string): Promise<[string, ClipchampAsset]> => {
    const [file, asset] = await assetResolver(id);
    file.getTranscoderInputFile();
    return [file.permanentUrl, asset];
  };
}

function getUrlFileExtension(url: string) {
  if (url) {
    const parsedUrl = new URL(url);
    const { pathname } = parsedUrl;
    const lastDotIndex = pathname.lastIndexOf('.');

    if (lastDotIndex === -1 || lastDotIndex === pathname.length - 1) {
      return '';
    }

    return pathname.substring(lastDotIndex + 1);
  }
  return '';
}

class ErrorBoundary extends PureComponent<ErrorBoundaryProps, ErrorBoundaryState> {
  private readonly i18n?: ErrorBoundaryProps['i18n'];
  private readonly logger?: ErrorBoundaryProps['logger'];

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
    this.logger = props && props.logger;
    this.i18n = props && props.i18n;
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (this.logger) {
      this.logger.logTrace('ClipchampPlayer - Error caught in ErrorBoundary:', error);
    }
  }

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <div>
          <h2>{this.i18n && this.i18n.getString(_TL_('Something went wrong.'))}</h2>
        </div>
      );
    }
    return this.props.children;
  }
}

export const ClipchampPlayer = ({
  videoUrl,
  width,
  logger,
  loadClipchampPlayer,
  i18n,
  poster,
  autoplay,
  useCustomWidthForClipchamp,
}: ClipchampPlayerProps) => {
  const activity = useContext(ActivityContext);
  const [project, setProject] = useState<ClipchampProject | null>(null);
  const [playerOptions, setPlayerOptions] = useState<ClipchampPlayerOptions | null>(null);
  const [clipchamp, setClipchamp] = useState<ClipchampPlayerModule | null>(null);
  const [loadingClipchamp, setLoadingClipchamp] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const classes = useStyles();

  let previewType = PREVIEW_TYPES.MP4;
  const videoType = getUrlFileExtension(videoUrl);
  if (videoType.toUpperCase() === PREVIEW_TYPES.CLIPCHAMP) {
    previewType = PREVIEW_TYPES.CLIPCHAMP;
  }


  useEffect(() => {
    if (previewType !== PREVIEW_TYPES.CLIPCHAMP) {
      return;
    }
    if (!loadClipchampPlayer) {
      setLoadingClipchamp(false);
      return;
    }

    const loadPlayer = async () => {
      const props = await loadClipchampPlayer();
      setClipchamp(props);
      setLoadingClipchamp(false);
    };

    loadPlayer();
  }, [loadClipchampPlayer, previewType]);

  useEffect(() => {
    if (previewType !== PREVIEW_TYPES.CLIPCHAMP) {
      return;
    }

    const fetchDataAsync = async () => {
      setIsLoading(true);

      const request = new Request(videoUrl);
      setPlayerOptions(null);
      setProject(null);

      fetch(request).then((response) => {
        if (response.status === 200) {
          return response.json();
        }
        throw new Error('Error occurred while loading project file');
      })
        .then((response: unknown) => {
          const proj = response as ClipchampProject;
          const playerOpts = {
            assetResolver: withPermanentUrl(clipchamp.createAssetResolver({
              getAssetById: (id: string) => {
                const asset = proj.assets.byId[id];
                if (!asset) {
                  throw new (Error as unknown as { new(message?: string, ...args: unknown[]): Error })('Asset not found for id: ', id);
                }
                return Promise.resolve(asset);
              },
              handlers: [clipchamp.UrlAssetFetchHandler, clipchamp.createHubbleAssetFetchHandler('test')],
            })),
            useVerse2Canvas: true,
          };
          setProject(proj);
          setPlayerOptions(playerOpts);
          setIsLoading(false);
        }).catch((error) => {
          logger && logger.logError('Error loading Clipchamp video', error);
        });
    };
    if (!loadingClipchamp && clipchamp && previewType === PREVIEW_TYPES.CLIPCHAMP) {
      fetchDataAsync();
    }
  }, [videoUrl, logger, clipchamp, loadingClipchamp, previewType]);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      setIsLoading(true);
      videoRef.current.load();
      setIsLoading(false);
    }
  }, [videoUrl]);

  const videoPreviewContent = useMemo(() => {
    if (previewType === PREVIEW_TYPES.CLIPCHAMP) {
      if ((!loadClipchampPlayer) || (!loadingClipchamp && !clipchamp)) {
        return <div className="clipchamp-player-load-failure">{i18n && i18n.getString(_TL_('Player cannot be initialized.'))}</div>;
      }

      if (clipchamp && clipchamp.Player) {
        const { Player } = clipchamp;
        return (
          <div
            className={mergeClasses('clipchamp-video-player', classes.playerContainer)}
            style={useCustomWidthForClipchamp ? { width } : undefined}
          >
            {(isLoading || loadingClipchamp || _.isNull(playerOptions) || _.isNull(project)) ? (
              <div className={classes.spinnerContainer}>
                <Overlay className={classes.spinnerOverlay}>
                  <Spinner className="page-load-spinner" {...({ useCustomSize: true } as unknown as React.ComponentProps<typeof Spinner>)} />
                </Overlay>
              </div>
            ) : (
              <ErrorBoundary logger={logger} i18n={i18n}>
                <Player
                  options={playerOptions}
                  project={project}
                  style={{
                    borderRadius: '4px 4px 0px 0px',
                    height: 'auto',
                    maxHeight: '450px',
                    ...(useCustomWidthForClipchamp ? { width } : {}),
                  }}
                  preload={10}
                  // The Clipchamp SDK toggles playback whenever `autoplay` is not `undefined`
                  // (`autoplay !== undefined && player.playPause()`), so passing `false` actually
                  // STARTS the video. Only forward the prop when autoplay is requested; otherwise
                  // leave it undefined so playback waits for the user to press play.
                  autoplay={autoplay || undefined}
                >
                  <div className={classes.playerActions}>
                    <span className={classes.playPauseVolume}>
                      <PlayPauseButton
                        usePlayPause={clipchamp.usePlayPause}
                        i18n={i18n}
                        activity={activity}
                        clipchampUrl={videoUrl}
                      />
                      <VolumeControls
                        usePlayerControls={clipchamp.usePlayerControls}
                        i18n={i18n}
                      />
                    </span>
                    <span className={classes.timeAndDuration}>
                      <TimeAndDuration
                        useFormattedTimeAndDuration={clipchamp.useFormattedTimeAndDuration}
                      />
                    </span>
                  </div>
                  <div className={classes.playerScrubber}>
                    <Scrubber
                      usePlayerTime={clipchamp.usePlayerTime}
                      usePlayerControls={clipchamp.usePlayerControls}
                    />
                  </div>
                </Player>
              </ErrorBoundary>
            )}
          </div>
        );
      }
      return null;
    }
    return (
      <div
        className="html-video-player"
        style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 'auto',
        }}
      >
        { isLoading && (
          <div className={classes.spinnerContainer} aria-label={i18n?.getString(_TL_('video is loading'))}>
            <Overlay className={classes.spinnerOverlay}>
              <Spinner className="page-load-spinner" {...({ useCustomSize: true } as unknown as React.ComponentProps<typeof Spinner>)} />
            </Overlay>
          </div>
        )}
        <video controls ref={videoRef} width={width} height="auto" poster={poster} style={{ maxHeight: '450px' }}>
          <track kind="captions" />
          <source src={videoUrl} type="video/mp4" />
          {i18n?.getString(_TL_('Your browser does not support the video tag.'))}
        </video>
      </div>
    );
  }, [previewType, loadClipchampPlayer, loadingClipchamp, clipchamp, classes, project, playerOptions, isLoading, videoUrl, width, useCustomWidthForClipchamp, i18n, logger, activity, autoplay, poster]);

  return (
    <div className="video-preview-container">
      {videoPreviewContent}
    </div>
  );
};

ClipchampPlayer.propTypes = {
  videoUrl: PropTypes.string.isRequired,
  width: PropTypes.string.isRequired,
  logger: PropTypes.shape({
    logError: PropTypes.func,
  }).isRequired,
  loadClipchampPlayer: PropTypes.func,
  autoplay: PropTypes.bool,
  useCustomWidthForClipchamp: PropTypes.bool,
};

ClipchampPlayer.defaultProps = {
  loadClipchampPlayer: _.noop,
  autoplay: true,
  useCustomWidthForClipchamp: false,
};
